<script lang="ts">
  // Key gate for the /demo experience. Two kinds of key:
  //   • a legacy 6-character tester code → the built-in family record (demoData.ts);
  //   • a 12-character access key issued in the admin console → validated by
  //     /api/demo/open, which returns the record to run on (e.g. the 12-year type 2
  //     diabetes case for physicians). Either way: no Google sign-in, no real data.
  // A key in the URL (…/demo?key=XXXX-XXXX-XXXX) opens the demo without typing.
  import { validCode, setDemoKey, openWithAccessKey } from '../lib/demoMode';
  import { startDemo } from '../lib/stores';
  import { t, tNow } from '../lib/i18n';
  import LangSwitch from './LangSwitch.svelte';

  let code = $state('');
  let error = $state('');
  let busy = $state(false);
  const raw = $derived(code.toUpperCase().replace(/[^A-Z0-9]/g, ''));

  function onInput() {
    const r = code.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
    // 6-char tester codes stay solid; longer access keys group as XXXX-XXXX-XXXX.
    code = r.length > 6 ? r.replace(/(.{4})(?=.)/g, '$1-') : r;
    error = '';
  }
  async function submit() {
    if (busy) return;
    if (raw.length === 6) {
      if (validCode(raw)) { setDemoKey(raw); startDemo(); return; }
      error = tNow('gate.badCode');
      return;
    }
    if (raw.length < 8) { error = tNow('gate.short'); return; }
    busy = true; error = '';
    try { await openWithAccessKey(raw); startDemo(); }
    catch (e) { error = (e as Error)?.message || String(e); }
    finally { busy = false; }
  }
  // One-click links from the admin console carry the key in the URL.
  $effect(() => {
    try {
      const k = new URL(location.href).searchParams.get('key');
      if (k && !code) { code = k.toUpperCase(); void submit(); }
    } catch (_) { /* ignore */ }
  });
</script>

<!-- Pre-auth screens are pinned to the dark brand look in Jakarta Sans. -->
<div class="wrap" data-force-dark>
  <div class="card">
    <div class="brand">empowered</div>
    <div class="tag">Demo</div>
    <div class="langrow"><LangSwitch /></div>
    <h1>{$t('gate.title')}</h1>
    <p class="sub">{$t('gate.sub')}</p>

    <form onsubmit={(e) => { e.preventDefault(); void submit(); }}>
      <label class="lbl" for="demokey">{$t('gate.label')}</label>
      <input
        id="demokey"
        class="code"
        name="demokey"
        type="text"
        maxlength="14"
        autocomplete="off"
        autocapitalize="characters"
        autocorrect="off"
        spellcheck="false"
        data-lpignore="true"
        data-1p-ignore
        data-form-type="other"
        placeholder="YOUR KEY"
        aria-label={$t('gate.label')}
        bind:value={code}
        oninput={onInput}
        disabled={busy}
      />
      <button class="go" type="submit" disabled={busy || raw.length < 6}>{busy ? $t('gate.opening') : $t('gate.enter')}</button>
    </form>

    {#if error}<p class="err">{error}</p>{/if}
    <p class="note">{$t('gate.note')}</p>
    <a class="back" href="/">{$t('gate.back')}</a>
  </div>
</div>

<style>
  .wrap { min-height: 100dvh; display: grid; place-items: center; background: var(--bg); padding: var(--space-6); }
  .langrow { display: flex; justify-content: center; }
  .card { width: 100%; max-width: 460px; text-align: center; display: flex; flex-direction: column; gap: var(--space-4); }
  .brand {
    font-family: var(--font-brand); font-size: var(--fs-lg); letter-spacing: 0.02em;
    background: var(--accent-grad-text); -webkit-background-clip: text; background-clip: text; color: transparent;
  }
  .tag {
    align-self: center; font-size: var(--fs-2xs); letter-spacing: 0.16em; text-transform: uppercase; font-weight: 600;
    color: var(--accent-2); border: 1px solid var(--accent); border-radius: var(--radius-pill); padding: 2px var(--space-3);
  }
  h1 { font-size: var(--fs-2xl); }
  .sub { color: var(--text-2); font-size: var(--fs-sm); line-height: var(--lh); }
  form { display: flex; flex-direction: column; gap: var(--space-2); margin-top: var(--space-2); }
  .lbl { font-size: var(--fs-xs); letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-3); text-align: center; }
  .code {
    width: 100%; min-width: 0; box-sizing: border-box;
    text-align: center; letter-spacing: 0.16em; text-indent: 0.16em; text-transform: uppercase;
    font-size: var(--fs-xl); font-family: var(--mono, ui-monospace, monospace); font-variant-numeric: tabular-nums;
    padding: var(--space-3) var(--space-2); border: 1px solid var(--line-strong); border-radius: var(--radius-md);
    background: var(--surface); color: var(--text);
  }
  .code::placeholder { color: var(--text-3); letter-spacing: 0.16em; }
  .code:focus-visible { box-shadow: var(--focus-ring); outline: none; }
  .go {
    padding: var(--space-3) var(--space-4); border-radius: var(--radius-pill);
    background: var(--accent-grad); color: var(--on-accent); font-weight: 600; font-size: var(--fs-md);
    transition: opacity var(--ease);
  }
  .go:disabled { opacity: 0.4; cursor: default; }
  .err { color: var(--danger); font-size: var(--fs-sm); }
  .note { color: var(--text-3); font-size: var(--fs-xs); margin-top: var(--space-1); line-height: var(--lh); }
  .back { color: var(--text-2); font-size: var(--fs-sm); margin-top: var(--space-2); }
  .back:hover { color: var(--text); }
</style>
